<template>
  <div class="roles animated fadeIn">
    <b-card>
      <div class="mb-4 d-flex">
        <h1 class="mb-0">Roles &amp; Permissions</h1>
        <div v-if="selectedRole" class="ml-auto text-right">
          <b-btn
            :disabled="!hasChanges"
            class="ml-2 px-4"
            variant="secondary"
            @click="resetRole"
          >
            Cancel
          </b-btn>
          <b-btn
            :disabled="!hasChanges"
            class="ml-2 px-4"
            variant="primary"
            @click="save"
          >
            Save
          </b-btn>
        </div>
      </div>
      <b-row>
        <b-col cols="3" class="border-right">
          <a
            v-for="role of roles"
            :key="role.id"
            :class="{ selected: selectedRole && selectedRole.id === role.id }"
            class="role"
            @click="selectRole(role)"
          >
            <div class="mr-auto">
              <h5 class="mb-0">{{ role.name }}</h5>
              <div>{{ role.description }}</div>
              <div class="small text-muted">
                {{ role.users.length | pluralize("user") }} |
                {{ role.permissions.length | pluralize("permission") }}
              </div>
            </div>
            <i
              v-if="hasRole(role.name)"
              v-b-tooltip.hover="'You have this role'"
              class="px-2 icon-user text-primary"
            />
            <b-btn
              v-if="
                !['Super Admin', 'Admin'].includes(role.name) &&
                hasPermission('role.delete')
              "
              v-b-tooltip.hover="'Delete role'"
              variant="link"
              class="px-2"
              @click.stop="deleteRole(role)"
            >
              <i class="icon-trash text-danger" />
            </b-btn>
          </a>
          <a
            v-if="hasPermission('role.create')"
            v-b-modal.create-role
            class="role"
          >
            <i class="mr-2 icon-plus" />
            Create role
          </a>
        </b-col>

        <b-col v-if="selectedRole" cols="6">
          <h5 class="mb-0">{{ selectedRole.name }} Permissions</h5>
          <div class="permissions d-flex flex-column flex-wrap">
            <template v-for="(permission, i) of permissions">
              <div
                v-if="i === 0 || !isAdjSameCategory(i, -1)"
                :key="`divider-${permission.id}`"
                class="h5 mb-0 mt-3"
              >
                {{ permission.name.split(".")[0] }}
              </div>

              <b-form-checkbox
                v-model="selectedRole.permissions"
                :disabled="!canEditPermissions"
                :value="permission"
                :key="`permission-${permission.id}`"
                class="permission mb-1"
              >
                <div>{{ permission.name }}</div>
                <div class="small">{{ permission.description }}</div>
              </b-form-checkbox>
            </template>
          </div>
        </b-col>

        <b-col v-if="selectedRole" cols="3" class="border-left">
          <h5 class="mb-3">{{ selectedRole.name }} Users</h5>
          <div class="users d-flex flex-column flex-wrap">
            <div
              v-for="(user, i) of selectedRole.users"
              :key="user.id"
              class="user"
            >
              <div>
                <div>{{ user.name }}</div>
                <div class="small">{{ user.email }}</div>
              </div>
              <b-btn
                v-if="canEditUsers"
                v-b-tooltip.hover="'Remove user'"
                variant="link"
                class="ml-auto"
                @click="selectedRole.users.splice(i, 1)"
              >
                <i class="icon-trash text-danger" />
              </b-btn>
            </div>

            <a v-if="canEditUsers" v-b-modal.role-users class="role">
              <i class="mr-2 icon-plus" />
              Add user
            </a>
          </div>
        </b-col>

        <b-col v-else class="d-flex align-items-center justify-content-center">
          <h5 class="mb-0 text-muted">Select a role to edit</h5>
        </b-col>
      </b-row>
    </b-card>

    <b-modal id="create-role" centered hide-footer hide-header>
      <h2 class="mb-4">Create Role</h2>

      <form @submit.prevent="createRole">
        <NanInput
          v-model="newRole.name"
          :validation="$v.newRole.name"
          label="Role name"
          placeholder="New Role"
          class="mb-3"
          autofocus
        />

        <NanInput
          v-model="newRole.description"
          :validation="$v.newRole.description"
          label="Role description"
          placeholder="A description for the role"
          class="mb-3"
          autofocus
        />

        <div class="text-right">
          <b-btn
            class="ml-2 px-4"
            variant="secondary"
            @click="$bvModal.hide('create-role')"
          >
            Cancel
          </b-btn>
          <b-btn
            :class="{ disabled: $v.newRole.$invalid }"
            class="ml-2 px-4"
            variant="primary"
            type="submit"
          >
            Create
          </b-btn>
        </div>
      </form>
    </b-modal>

    <b-modal id="role-users" centered hide-footer hide-header>
      <h2 class="mb-4">Add User(s)</h2>

      <b-input
        v-model="userSearch"
        debounce="500"
        placeholder="search..."
        type="search"
      />
      <b-table
        :fields="[
          { key: 'name', label: 'Name' },
          { key: 'email', label: 'Email' },
          { key: 'actions', label: '' }
        ]"
        :items="searchUsers"
        :filter="userSearch"
        :current-page="userPage"
        :per-page="10"
      >
        <template #cell(actions)="{ item }">
          <b-btn
            :disabled="item.added"
            class="p-0"
            variant="link"
            @click="selectedRole.users.push(item)"
          >
            <i
              :class="
                selectedRole.users.find(u => u.id === item.id)
                  ? 'icon-check text-success'
                  : 'icon-plus text-primary'
              "
            />
          </b-btn>
        </template>
      </b-table>
      <b-pagination
        v-if="userTotal > 10"
        v-model="userPage"
        :total-rows="userTotal"
        :per-page="10"
        align="center"
        class="my-0"
        prev-text="Prev"
        next-text="Next"
        hide-goto-end-buttons
      />
    </b-modal>
  </div>
</template>

<script>
import { validationMixin } from "vuelidate";
import { required } from "vuelidate/lib/validators";
import { mapGetters } from "vuex";

import API from "@/api";
import exitGuard from "@/mixins/exitGuard";

export default {
  mixins: [exitGuard, validationMixin],

  data: () => ({
    newRole: {
      name: "",
      description: ""
    },
    roles: [],
    permissions: [],
    savedRole: null,
    selectedRole: null,
    savedPermissions: null,
    savedUsers: null,
    userSearch: "",
    userPage: 1,
    userTotal: 0
  }),

  computed: {
    ...mapGetters("user", ["hasPermission", "hasRole"]),

    canEditPermissions() {
      return !this.isAdminRole && this.hasPermission("role.edit");
    },

    canEditUsers() {
      if (this.isAdminRole) return this.hasRole("Super Admin");
      return this.hasPermission("role.assign");
    },

    hasChanges() {
      return this.hasPermissionChanges || this.hasUserChanges;
    },

    hasPermissionChanges() {
      if (!this.selectedRole) return false;
      const permissions = this.selectedRole.permissions.map(p => p.id).sort();
      return JSON.stringify(permissions) !== this.savedPermissions;
    },

    hasUserChanges() {
      if (!this.selectedRole) return false;
      const users = this.selectedRole.users.map(u => u.id).sort();
      return JSON.stringify(users) !== this.savedUsers;
    },

    isAdminRole() {
      return ["Super Admin", "Admin"].includes(this.selectedRole.name);
    }
  },

  watch: {
    selectedRole: "snapshot"
  },

  created() {
    this.getPermissions();
    this.getRoles();
  },

  methods: {
    async createRole() {
      const { name, description } = this.newRole;
      const { success } = await API.createRole(name, description);
      if (!success) return;

      this.$notify({
        type: "success",
        text: "Role created"
      });

      this.getRoles();
      this.$bvModal.hide("create-role");
    },

    async deleteRole(role) {
      const confirm = await this.$bvModal.msgBoxConfirm(
        `Are you sure you want to delete the role ${role.name}?`,
        {
          title: "Delete Role",
          okVariant: "danger",
          okTitle: "Delete",
          size: "sm",
          centered: true
        }
      );
      if (!confirm) return;

      const { success } = await API.deleteRole(role.id);
      if (!success) return;

      this.$notify({
        type: "success",
        text: "Role deleted"
      });

      this.getRoles();
    },

    async getPermissions() {
      const { success, results } = await API.getPermissions();
      if (!success) return;
      this.permissions = results.sort((a, b) => a.name.localeCompare(b.name));
    },

    async getRoles() {
      const { success, results } = await API.getRoles();
      if (!success) return;
      this.roles = results;
    },

    isAdjSameCategory(i, offset) {
      const current = this.permissions[i].name.split(".")[0];
      const prev = this.permissions[i + offset].name.split(".")[0];
      return current === prev;
    },

    resetRole() {
      const role = this.roles.find(r => r.id === this.selectedRole.id);
      this.selectedRole = JSON.parse(JSON.stringify(role));
    },

    async save() {
      let { id, permissions, users } = this.selectedRole;

      if (this.hasPermissionChanges) {
        permissions = permissions.map(p => p.id);
        const { success } = await API.editRolePermissions(id, permissions);
        if (success) {
          this.$notify({
            type: "success",
            text: "Role permissions updated"
          });
        }
      }

      if (this.hasUserChanges) {
        users = users.map(u => u.id);
        const { success } = await API.editRoleUsers(id, users);
        if (success) {
          this.$notify({
            type: "success",
            text: "Role users updated"
          });

          const self = this.$store.state.user;
          const selfInRole = !!users.find(id => id === self.id);
          const selfHasRole = this.hasRole(this.selectedRole.name);
          let roles = [...self.roles];

          if (selfInRole && !selfHasRole) {
            roles.push(this.selectedRole.name);
          } else if (!selfInRole && selfHasRole) {
            roles.splice(roles.indexOf(this.selectedRole.name), 1);
          }

          this.$store.commit("user/PATCH_USER", { roles });
        }
      }

      this.getRoles();
      this.snapshot();
    },

    async searchUsers(ctx) {
      const { success, results } = await API.adminUserSearch(
        this.userSearch,
        ctx.currentPage,
        ctx.perPage
      );
      if (!success) return;

      const users = results.results;
      users.forEach(user => {
        user.added = !!this.selectedRole.users.find(m => m.id === user.id);
      });

      this.userTotal = results.total;
      return users;
    },

    async selectRole(role) {
      if (this.hasChanges) {
        const confirm = await this.$bvModal.msgBoxConfirm(
          "You have unsaved changes. Are you sure you want to continue?",
          {
            title: "Unsaved Changes",
            okVariant: "danger",
            okTitle: "Continue",
            size: "sm",
            centered: true
          }
        );
        if (!confirm) return;
      }

      this.selectedRole = JSON.parse(JSON.stringify(role));
    },

    snapshot() {
      const permissions = this.selectedRole.permissions.map(p => p.id).sort();
      const users = this.selectedRole.users.map(u => u.id).sort();
      this.savedPermissions = JSON.stringify(permissions);
      this.savedUsers = JSON.stringify(users);
    }
  },

  validations: {
    newRole: {
      name: { required },
      description: { required }
    }
  }
};
</script>

<style lang="scss">
.roles {
  .role,
  .user {
    all: unset;
    display: flex;
    align-items: center;
    padding: 0.5rem 0.5rem 0.5rem 1rem;
    border: 1px solid #ccc;
    border-radius: 4px;
    margin-bottom: 0.5rem;
    transition: all 0.2s ease-in-out;

    &.selected {
      background: #eee;
    }
  }

  .role {
    cursor: pointer;
    &:hover {
      background: #f7f7f7;
    }
  }

  .permissions {
    height: 780px;
  }
}
</style>
